import React from 'react';
import { Navigate } from 'react-router-dom';
import { isFirebaseConfigured } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption } from '../../context/RoleAssumptionContext';
import { UserRole } from '../../services/firestore';
import LoadingSpinner from '../common/LoadingSpinner';

interface RoleRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallbackPath?: string;
}

/**
 * RoleRoute - Ensures user has one of the allowed roles
 *
 * Use this inside a ProtectedRoute to add role-based access control
 * If Firebase is not configured, allows access (development mode)
 *
 * Note: This uses effectiveRole from RoleAssumptionContext, which respects
 * role assumption by Platform Admins. Use the `useActualRole` prop to
 * bypass role assumption and check the actual Firestore role.
 */
export const RoleRoute: React.FC<RoleRouteProps & { useActualRole?: boolean }> = ({
  children,
  allowedRoles,
  fallbackPath = '/',
  useActualRole = false,
}) => {
  const { role: actualRole, loading, profileLoading } = useAuth();
  const { effectiveRole } = useRoleAssumption();

  // Use actual role or effective role based on prop
  const role = useActualRole ? actualRole : effectiveRole;

  // If Firebase is not configured, allow access (development mode)
  if (!isFirebaseConfigured) {
    return <>{children}</>;
  }

  // Show loading state while checking auth
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Check if user has an allowed role
  if (!role || !allowedRoles.includes(role)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

/**
 * AdminRoute - Shortcut for routes that require any admin role
 */
export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <RoleRoute allowedRoles={['platform_admin', 'cra_admin', 'bank_admin']}>
      {children}
    </RoleRoute>
  );
};

/**
 * PlatformAdminRoute - Shortcut for routes that require platform admin
 */
export const PlatformAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <RoleRoute allowedRoles={['platform_admin']}>
      {children}
    </RoleRoute>
  );
};

export default RoleRoute;
